import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, Users, AlertCircle, Loader2, ArrowLeft, RefreshCcw, Clock } from 'lucide-react';
import io from 'socket.io-client';
import QueueStatus from './QueueStatus';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.glosscut.com';

const TrackQueue = () => {
    const { trackingId: urlTrackingId } = useParams();
    const navigate = useNavigate();

    const [trackingId, setTrackingId] = useState(urlTrackingId || '');
    const [queueData, setQueueData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(false);

    // Live Tracker State for 'started' appointments
    const [nowTick, setNowTick] = useState(Date.now());
    const [timeOffset, setTimeOffset] = useState(0);

    // Sync time with server once on mount
    useEffect(() => {
        const syncTime = async () => {
            try {
                const res = await fetch(`${API_URL}/api/booking/server-time`);
                const data = await res.json();
                if (data.success && data.serverTimeMs) {
                    const localTime = Date.now();
                    const offset = data.serverTimeMs - localTime;
                    setTimeOffset(offset);
                    setNowTick(localTime + offset);
                }
            } catch (err) {
                console.error("Failed to sync server time", err);
            }
        };
        syncTime();
    }, []);

    useEffect(() => {
        let interval;
        if (queueData?.status === 'started') {
            interval = setInterval(() => {
                setNowTick(Date.now() + timeOffset);
            }, 30000); // 30s update interval
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [queueData?.status, timeOffset]);

    const fetchQueuePosition = useCallback(async (id, silent = false) => {
        if (!silent) setLoading(true);
        setError(null);

        try {
            // Only uppercase if it looks like a short code
            const searchId = id.length === 6 ? id.toUpperCase() : id;
            const res = await fetch(`${API_URL}/api/booking/track/${searchId}`);
            const data = await res.json();

            if (res.ok) {
                setQueueData(data.data);
                setAutoRefresh(true);
            } else {
                setError(data.msg || 'Booking not found');
                setQueueData(null);
                setAutoRefresh(false);
            }
        } catch (err) {
            setError('Network error. Please try again.');
            setAutoRefresh(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (urlTrackingId) {
            fetchQueuePosition(urlTrackingId);
        } else {
            // Reset state if URL no longer contains trackingId (e.g., user went back to /track-queue)
            setQueueData(null);
            setTrackingId('');
            setAutoRefresh(false);
            setError(null);
        }
    }, [urlTrackingId, fetchQueuePosition]);

    // Live Updates (Socket + Polling Fallback)
    useEffect(() => {
        let interval;
        let socket;

        if (queueData && autoRefresh) {
            // 1. WebSocket for Instant Updates
            socket = io(API_URL, { transports: ['websocket'] });

            socket.on('connect', () => {
                socket.emit('join', `booking_${queueData.bookingId}`);
            });

            socket.on('booking_status_update', (data) => {
                if (data.status) {
                    fetchQueuePosition(queueData.trackingId, true);
                }
            });

            // Listen for Almost Ready Call
            socket.on('almost_ready_call', (data) => {
                // You could use a nicer toast here if one exists in the app, using native alert for simplicity
                alert(`🚨 YOU ARE UP NEXT! 🚨\n\nYour barber is almost ready for you! Please head to the shop within the next 10 minutes to avoid losing your spot. \n\nMessage: ${data.message || ""}`);
                fetchQueuePosition(queueData.trackingId, true);
            });

            // 2. Polling Fallback (15s)
            interval = setInterval(() => {
                fetchQueuePosition(queueData.trackingId, true);
            }, 15000);
        }

        return () => {
            if (interval) clearInterval(interval);
            if (socket) socket.disconnect();
        };
    }, [queueData, autoRefresh, fetchQueuePosition]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (trackingId.trim()) {
            const searchId = trackingId.trim();
            const formattedId = searchId.length === 6 ? searchId.toUpperCase() : searchId;
            navigate(`/track-queue/${formattedId}`);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'started': return 'text-blue-600 bg-blue-100';
            case 'confirmed': return 'text-green-600 bg-green-100';
            case 'pending': return 'text-yellow-600 bg-yellow-100';
            case 'completed': return 'text-gray-600 bg-gray-100';
            case 'cancelled': return 'text-red-600 bg-red-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'started': return 'In Progress';
            case 'confirmed': return 'Confirmed';
            case 'pending': return 'Awaiting Approval';
            case 'completed': return 'Completed';
            case 'cancelled': return 'Cancelled';
            default: return status;
        }
    };

    return (
        <div className="min-h-screen relative overflow-hidden bg-gray-50">
            {/* Background Gradients */}
            <div className="absolute inset-0 bg-gradient-to-b from-gray-50 via-white to-gray-50" />
            <div className="absolute top-[-5%] right-[-15%] w-[90vw] h-[90vw] lg:w-[50vw] lg:h-[50vw] rounded-full blur-[80px] opacity-20 mix-blend-multiply" style={{ background: 'radial-gradient(circle, #4C763B 0%, #22C55E 100%)' }} />
            <div className="absolute bottom-[10%] left-[-10%] w-[70vw] h-[70vw] lg:w-[40vw] lg:h-[40vw] rounded-full blur-[90px] opacity-15 mix-blend-multiply" style={{ background: 'radial-gradient(circle, #a855f7 0%, #ec4899 100%)' }} />

            {/* Content */}
            <div className="relative z-10 min-h-screen p-4 pt-24 lg:pt-32">
                {/* Header */}
                <div className="max-w-5xl mx-auto mb-8">
                    <h1 className="text-4xl lg:text-5xl font-black bg-gradient-to-r from-[#4C763B] to-[#22C55E] bg-clip-text text-transparent mb-2">
                        Queue Tracker
                    </h1>
                    <p className="text-gray-600">Track your position in real-time</p>
                </div>

                {/* Search Form */}
                {!queueData && (
                    <div className="max-w-md mx-auto">
                        <form onSubmit={handleSubmit} className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border-2 border-gray-200">
                            <div className="mb-6">
                                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                                    Enter Tracking ID or Order ID
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={trackingId}
                                        onChange={(e) => setTrackingId(e.target.value)}
                                        placeholder="e.g. A12B34 or Order ID"
                                        maxLength={24}
                                        className="w-full px-4 py-3 text-center text-xl font-mono font-bold border-2 border-gray-300 rounded-xl focus:border-[#4C763B] focus:ring-2 focus:ring-[#4C763B]/20 outline-none transition-all text-gray-900"
                                        required
                                    />
                                </div>
                                <p className="text-xs text-gray-500 mt-2">Enter the tracking code or the full Order ID from your receipt</p>
                            </div>

                            {error && (
                                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center text-red-700 text-sm">
                                    <AlertCircle size={18} className="mr-2 flex-shrink-0" />
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading || trackingId.trim().length < 6}
                                className="w-full bg-gradient-to-r from-[#4C763B] to-[#22C55E] text-white font-bold py-3 px-6 rounded-xl hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 size={20} className="animate-spin mr-2" />
                                        Tracking...
                                    </>
                                ) : (
                                    <>
                                        <Search size={20} className="mr-2" />
                                        Track Queue
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                )}

                {/* Queue Display */}
                {/* Queue Display */}
                {queueData && (
                    <div className="max-w-5xl mx-auto">
                        {/* Back Button */}
                        <div className="mb-6">
                            <button
                                onClick={() => {
                                    setQueueData(null);
                                    setTrackingId('');
                                    setAutoRefresh(false);
                                    navigate('/track-queue');
                                }}
                                className="text-sm text-gray-600 hover:text-[#4C763B] flex items-center transition-colors font-medium px-4 py-2 bg-white/50 hover:bg-white rounded-lg shadow-sm w-fit"
                            >
                                <ArrowLeft size={16} className="mr-2" />
                                Track different booking
                            </button>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
                            {/* Left Column - Position Card & Details */}
                            <div className="lg:col-span-6 space-y-4">
                                {/* Position Card */}
                                <div className="bg-black rounded-3xl p-6 lg:p-12 shadow-2xl relative overflow-hidden group border border-gray-800 mb-6">
                                    {queueData.status === 'cancelled' ? (
                                        <div className="relative z-10 text-center flex flex-col justify-center h-full min-h-[200px] lg:min-h-[350px]">
                                            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-red-500/50">
                                                <AlertCircle size={40} className="text-red-500" />
                                            </div>
                                            <h2 className="text-3xl lg:text-5xl font-black text-white mb-4 tracking-tighter uppercase">
                                                Appointment Cancelled
                                            </h2>
                                            {queueData.cancellationReason && (
                                                <p className="text-red-400 text-lg lg:text-xl font-medium mb-6 italic">
                                                    "{queueData.cancellationReason}"
                                                </p>
                                            )}
                                            <p className="text-gray-400 max-w-sm mx-auto text-sm leading-relaxed">
                                                Unfortunately, this appointment has been cancelled by the barber. Please contact the shop or book a new slot.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="relative z-10 text-center flex flex-col justify-center h-full min-h-[200px] lg:min-h-[350px]">
                                            <div className="flex items-center justify-center gap-2 mb-4 lg:mb-6">
                                                <div className="h-px w-6 lg:w-8 bg-gray-600 rounded-full" />
                                                <div className="text-xs lg:text-base uppercase tracking-[0.3em] text-gray-400 font-bold">Your Status</div>
                                                <div className="h-px w-6 lg:w-8 bg-gray-600 rounded-full" />
                                            </div>

                                            {/* Token Number - Compact on Mobile, Large on Desktop */}
                                            <div className="mb-4 lg:mb-6">
                                                <div className="text-6xl lg:text-9xl font-black text-white tracking-tighter">
                                                    #{queueData.queuePosition}
                                                </div>
                                            </div>

                                            <div className="text-lg lg:text-2xl font-medium text-gray-300">
                                                {queueData.status === 'started' ? (
                                                    <span className="font-bold inline-flex flex-col items-center gap-2 text-white">
                                                        <span className="text-sm lg:text-base uppercase tracking-widest text-emerald-400">✂️ IN PROGRESS</span>
                                                        {(() => {
                                                            const baseMins = queueData.baseDuration || 30;
                                                            const offset = queueData.durationOffset || 0;
                                                            const totalMins = baseMins + offset;

                                                            const elapsedMs = nowTick - new Date(queueData.startedAt).getTime();
                                                            const elapsedMinutes = Math.floor(elapsedMs / 60000);
                                                            const remainingTime = totalMins - elapsedMinutes;

                                                            if (remainingTime < 0) {
                                                                return <span className="text-red-400 font-black text-2xl animate-pulse">OVERTIME (+{Math.abs(remainingTime)}m)</span>;
                                                            }
                                                            return <span className="text-4xl lg:text-5xl font-black">{remainingTime} <span className="text-xl lg:text-2xl opacity-60">MIN LEFT</span></span>;
                                                        })()}
                                                    </span>
                                                ) : queueData.peopleAhead === 0 ? (
                                                    <span className="font-bold inline-flex items-center gap-2 text-white animate-pulse">
                                                        🎉 It's Your Turn!
                                                    </span>
                                                ) : (
                                                    <span className="flex items-center justify-center gap-2">
                                                        <span className="text-white font-bold text-2xl lg:text-3xl">{queueData.peopleAhead}</span>
                                                        <span className="opacity-60 text-sm lg:text-base uppercase tracking-wide mt-1">People Ahead</span>
                                                    </span>
                                                )}
                                            </div>

                                            {/* Estimated Wait Time Display */}
                                            {queueData.status !== 'started' && queueData.status !== 'completed' && queueData.status !== 'cancelled' && (
                                                <div className="mt-8 pt-6 border-t border-gray-800/50">
                                                    <div className="flex flex-col items-center">
                                                        <div className="flex items-center text-gray-400 mb-2">
                                                            <Clock size={16} className="mr-2" />
                                                            <span className="text-sm font-bold uppercase tracking-widest">Estimated Wait</span>
                                                        </div>
                                                        <div className="text-3xl lg:text-4xl font-black text-[#22C55E]">
                                                            {queueData.estimatedWaitRange
                                                                ? `${queueData.estimatedWaitRange.min} - ${queueData.estimatedWaitRange.max}`
                                                                : queueData.estimatedWaitMinutes || '--'}
                                                            <span className="text-lg lg:text-xl text-gray-400 ml-2">mins</span>
                                                        </div>

                                                        {/* Dynamic Alert based on time */}
                                                        {queueData.estimatedWaitMinutes <= 15 ? (
                                                            <div className="mt-4 bg-orange-500/20 border border-orange-500/50 p-3 rounded-xl flex items-start text-left max-w-sm mx-auto">
                                                                <AlertCircle size={20} className="text-orange-400 mr-3 mt-0.5 flex-shrink-0" />
                                                                <p className="text-sm text-orange-200 font-medium">
                                                                    You're almost up! Please head to the shop within the next 10 minutes to avoid losing your spot.
                                                                </p>
                                                            </div>
                                                        ) : (
                                                            <p className="mt-4 text-xs text-gray-500 max-w-xs mx-auto text-center italic">
                                                                ⚠️ Note: This is an approximate time based on standard service durations. Actual time may vary.
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {/* Queue Status - Full Width */}
                                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border-2 border-gray-100 hover:border-gray-200 transition-all">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center text-gray-600">
                                            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center mr-3 text-blue-600">
                                                <Users size={20} />
                                            </div>
                                            <span className="text-sm font-bold uppercase tracking-wider">Total in Queue</span>
                                        </div>
                                        <div className="text-4xl font-black text-gray-900">{queueData.totalInQueue}</div>
                                    </div>
                                </div>

                                {/* Booking Details */}
                                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border-2 border-gray-100 space-y-4">
                                    <h3 className="text-lg font-bold text-gray-900 border-b border-gray-100 pb-3">Booking Details</h3>
                                    <div className="space-y-3.5">
                                        <div className="flex justify-between items-center group">
                                            <span className="text-gray-500 text-sm">Tracking ID</span>
                                            <span className="font-mono font-bold text-[#4C763B] bg-green-50 px-2 py-1 rounded text-sm group-hover:bg-green-100 transition-colors uppercase">
                                                #{queueData.trackingId}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center group pt-1">
                                            <span className="text-gray-500 text-[10px] uppercase font-bold tracking-wider">Order ID</span>
                                            <span className="font-mono text-[10px] text-gray-400 font-bold truncate max-w-[150px]">
                                                {queueData.bookingId}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-gray-500">Shop</span>
                                            <span className="font-semibold text-gray-900 text-right">{queueData.shopName}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-gray-500">Barber</span>
                                            <span className="font-semibold text-gray-900">{queueData.barberName}</span>
                                        </div>
                                        <div className="flex justify-between items-start text-sm">
                                            <span className="text-gray-500 whitespace-nowrap mr-4">Services</span>
                                            <span className="font-semibold text-gray-900 text-right leading-tight">{queueData.services.join(', ')}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-gray-500">Booked at</span>
                                            <span className="font-semibold text-gray-900">{queueData.bookingTime}</span>
                                        </div>
                                        <div className="pt-2 border-t border-gray-100 flex justify-between items-center">
                                            <span className="text-gray-500 text-sm">Status</span>
                                            <span className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide ${getStatusColor(queueData.status)}`}>
                                                {getStatusText(queueData.status)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Auto-refresh indicator & Manual Refresh */}
                                <div className="flex items-center justify-between bg-white/60 backdrop-blur-sm rounded-xl px-4 py-3 border border-gray-200">
                                    <div className="flex items-center text-xs font-medium text-gray-500">
                                        <div className="relative flex h-2 w-2 mr-2">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                                        </div>
                                        <span>Live Updates (10s)</span>
                                    </div>
                                    <button
                                        onClick={() => fetchQueuePosition(queueData.trackingId, false)}
                                        className="flex items-center text-xs text-[#4C763B] hover:text-[#22C55E] font-bold uppercase tracking-wide transition-colors hover:bg-green-50 px-2 py-1 rounded"
                                    >
                                        <RefreshCcw size={14} className="mr-1.5" />
                                        Refresh
                                    </button>
                                </div>

                                {/* Current Token Info */}
                                <div className="text-center text-xs text-gray-400 font-medium pt-2">
                                    Now Serving Token #{queueData.currentToken}
                                </div>
                            </div>

                            {/* Right Column - Live Queue List */}
                            <div className="lg:col-span-6 space-y-4">

                                {/* Live Queue Data - Added Component */}
                                <div className="overflow-hidden rounded-2xl shadow-xl border border-gray-800">
                                    <QueueStatus barberId={queueData.barberId} />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TrackQueue;
